/**
 * Physical constants and simulation defaults. SI units throughout.
 *
 * Coordinate system (matches the Three.js default):
 * - Right-handed, y up.
 * - x points east, z points south, so north is -z.
 * - The storm center sits at the origin at t = 0.
 * - "Counterclockwise viewed from above" is positive rotation about +y,
 *   which carries +x (east) toward -z (north).
 *
 * Rendering converts metres to scene units with RENDER_UNITS_PER_M. Nothing
 * in physics/ reads that constant.
 */

/**
 * Earth's sidereal rotation rate, rad/s.
 * Source: IERS Conventions (2010), IERS Technical Note No. 36, Table 1.1
 * (nominal mean angular velocity 7.292115e-5 rad/s).
 */
export const EARTH_OMEGA_RAD_S = 7.292115e-5;

/**
 * Standard acceleration of gravity, m/s^2 (exact by definition).
 * Source: BIPM, The International System of Units (SI Brochure), 9th ed. (2019).
 */
export const STANDARD_GRAVITY_MPS2 = 9.80665;

/**
 * Default boundary-layer air density for the Holland gradient wind, kg/m^3.
 * Source: BUILD_SPEC.md section 6.1 default. Holland (1980) uses a surface
 * density of the same order; replace with a cited value if this is ever
 * calibrated against observations.
 */
export const DEFAULT_AIR_DENSITY_KG_M3 = 1.15;

/**
 * Holland B shape parameter bounds.
 * Source: Holland, G. J. (1980). An Analytic Model of the Wind and Pressure
 * Profiles in Hurricanes. Monthly Weather Review, 108, 1212-1218. Holland
 * reports physically plausible B between roughly 1 and 2.5.
 */
export const HOLLAND_B_MIN = 1;
export const HOLLAND_B_MAX = 2.5;

/** Below this radius the vortex returns zero wind (r -> 0 singularity guard). */
export const HOLLAND_MIN_RADIUS_M = 1;

/**
 * Illustrative storm used as the default scene. These are demo inputs, not a
 * reconstruction of any real storm.
 */
export const DEFAULT_STORM = {
  pAmbientPa: 101_000,
  pCentralPa: 96_000,
  rMaxM: 30_000,
  B: 1.5,
  latitudeDeg: 25,
} as const;

/**
 * Phase 3 defaults: secondary circulation, translation, turbulence, gusts.
 * Illustrative demo inputs chosen for a readable picture, not fitted to any
 * storm or climatology.
 */
export const DEFAULT_SECONDARY = {
  inflowDepthM: 1_000,
  topM: 15_000,
  inflowPeakMps: 15,
  tangentialFullHeightM: 2_000,
} as const;

export const DEFAULT_STEERING = { speedMps: 5, headingDeg: 300 } as const;

export const DEFAULT_TURBULENCE = {
  modes: 8,
  maxWavelengthM: 40_000,
  minWavelengthM: 4_000,
  amplitudeMps: 6,
  heightScaleM: 6_000,
  periodS: 900,
} as const;

/** OU gust factor: mean 1, reverts over ~2 min of storm time, stationary sd 0.35. */
export const DEFAULT_GUST = { mu: 1, theta: 1 / 120, stationaryStd: 0.35 } as const;

/** Domain radius as a multiple of Rmax. BUILD_SPEC.md section 7 asks for ~5x. */
export const DOMAIN_RADIUS_IN_RMAX = 5;

/**
 * Display-only vertical exaggeration. A hurricane is about 300 km across and
 * 15 km deep, so at true scale the updraft structure is a thin film. Weather
 * graphics routinely stretch the vertical axis for this reason. Physics never
 * reads this value; only render positions are scaled.
 */
export const DEFAULT_VERTICAL_STRETCH = 5;

/**
 * Cloud layer (phase 6). Heights and rates are visual and modeling choices,
 * not fitted values. The eddy viscosity and diffusivity are rough
 * mesoscale orders of magnitude, stated as assumptions.
 */
export const DEFAULT_CLOUDS = {
  baseM: 2_000,
  topM: 12_000,
  /** Height of the 2D fluid layer. Updraft and outflow are sampled here. */
  layerHeightM: 5_000,
  /** Peak density source rate over the eyewall updraft, 1/s. */
  sourcePeakPerS: 2 / 3600,
  densityDecayS: 3_600,
  viscosity: 2_000,
  diffusivity: 500,
  nudgeTimeS: 900,
  jacobiIterations: 20,
} as const;

/** Scene units per metre. Rendering works in kilometres to keep floats tame. */
export const RENDER_UNITS_PER_M = 1e-3;

/**
 * Simulated seconds per wall-clock second. A real eyewall orbit takes
 * roughly an hour, so real time would look frozen.
 */
export const DEFAULT_TIME_SCALE = 120;
export const REDUCED_MOTION_TIME_SCALE = 30;
